
  import { defineComponent, onMounted, ref, computed } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import LookupNameIds from '@/store/enums/settingsEnums';

  export default defineComponent({
    name: 'package-validation-rule-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const ruleData = ref<any>(null);

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('Update Validation Rule'), [
          translate('settings'),
        ]);

        // Load dropdown data
        await store.dispatch(Actions.GET_ALL_SETTINGS_CLASS_CATEGORY);
        await store.dispatch(Actions.GET_ALL_SETTINGS_LOOKUPS, [
          LookupNameIds.HORSE_TYPE,
          LookupNameIds.CLASS_TYPE,
        ]);
        await store.dispatch(Actions.GET_PACKAGE_VALIDATION_RULE_TYPES);

        // Load rule data
        const id = route.params.id as string;
        const data = await store.dispatch(
          Actions.GET_PACKAGE_VALIDATION_RULE,
          id
        );
        ruleData.value = data.data;
      });

      const validationSchema = computed(() => {
        const baseSchema: any = {
          name: Yup.string().required(() => translate('nameIsRequired')),
          ruleType: Yup.string().required(() =>
            translate('ruleTypeIsRequired')
          ),
          numberOfCancellation: Yup.number().required(() =>
            translate('numberOfCancellationIsRequired')
          ),
          numberOfDays: Yup.number().required(() =>
            translate('numberOfDaysIsRequired')
          ),
          priority: Yup.number().required(() =>
            translate('priorityIsRequired')
          ),
        };

        // Add conditional validation based on rule type
        if (ruleData.value?.ruleType === 'CLASS_CATEGORY') {
          baseSchema.classCategoryId = Yup.string()
            .nullable()
            .required(() => translate('Class Category is required'));
        } else if (
          ruleData.value?.ruleType === 'PACKAGE_COUNT' ||
          ruleData.value?.ruleType === 'INVALID_PACKAGE'
        ) {
          baseSchema.packageCountMin = Yup.number()
            .nullable()
            .required(() => translate('Package Count Min is required'));
          baseSchema.packageCountMax = Yup.number()
            .nullable()
            .required(() => translate('Package Count Max is required'));
        } else if (ruleData.value?.ruleType === 'HORSE_LESSON_COMBO') {
          baseSchema.horseTypeId = Yup.string()
            .nullable()
            .required(() => translate('Horse Type is required'));
          baseSchema.packageCountMin = Yup.number()
            .nullable()
            .required(() => translate('Package Count Min is required'));
          baseSchema.packageCountMax = Yup.number()
            .nullable()
            .required(() => translate('Package Count Max is required'));
        }

        return Yup.object().shape(baseSchema);
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        // Clean up values based on rule type
        const payload: any = {
          id: ruleData.value.id,
          name: ruleData.value.name,
          ruleType: ruleData.value.ruleType,
          numberOfCancellation: Number(ruleData.value.numberOfCancellation),
          numberOfDays: Number(ruleData.value.numberOfDays),
          numberOfDaysError: ruleData.value.numberOfDaysError || null,
          priority: Number(ruleData.value.priority),
          isActive: ruleData.value.isActive,
        };

        if (ruleData.value.ruleType === 'CLASS_CATEGORY') {
          payload.classCategoryId = ruleData.value.classCategoryId;
        } else if (
          ruleData.value.ruleType === 'PACKAGE_COUNT' ||
          ruleData.value.ruleType === 'INVALID_PACKAGE'
        ) {
          payload.packageCountMin = Number(ruleData.value.packageCountMin);
          payload.packageCountMax = Number(ruleData.value.packageCountMax);
        } else if (ruleData.value.ruleType === 'HORSE_LESSON_COMBO') {
          payload.horseTypeId = ruleData.value.horseTypeId;
          if (ruleData.value.lessonTypeId)
            payload.lessonTypeId = ruleData.value.lessonTypeId;
          if (ruleData.value.packageCountMin)
            payload.packageCountMin = Number(ruleData.value.packageCountMin);
          if (ruleData.value.packageCountMax)
            payload.packageCountMax = Number(ruleData.value.packageCountMax);
        }

        const result = await store.dispatch(
          Actions.UPDATE_PACKAGE_VALIDATION_RULE,
          payload
        );
        const errors = store.getters.getErrors;
        const hasErrors = errors && Object.keys(errors).length > 0;

        if (hasErrors || !result) {
          const [errorName] = Object.keys(errors || {});
          const error = errors[errorName];
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('Successfully Updated Validation Rule'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'package-validation-rule-listing' });
          });
        }
      };

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        can,
        ruleData,
        allClassCategories: computed(() => store.getters.allClassCategoryList),
        ruleTypes: computed(() => store.getters.packageValidationRuleTypesList),
        horseTypes: computed(() =>
          store.getters.allLookupsList.filter(
            (lookup) => lookup.lookupNameId === 16
          )
        ),
        lessonTypes: computed(() =>
          store.getters.allLookupsList.filter(
            (lookup) => lookup.lookupNameId === 18
          )
        ),
      };
    },
  });
